import { useEffect, useState } from "react";
import { ArrowUpRight, Mail, MapPin, Send, CheckCircle2 } from "lucide-react";
import { fetchContact, sendContactMessage } from "@/lib/api";

export default function Contact() {
  const [contact, setContact] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [status, setStatus] = useState("idle"); // idle | sending | sent | error
  const [errorMsg, setErrorMsg] = useState("");

  useEffect(() => {
    fetchContact().then(setContact).catch(() => {});
  }, []);

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    setStatus("sending");
    try {
      await sendContactMessage(form);
      setStatus("sent");
      setForm({ name: "", email: "", message: "" });
    } catch (err) {
      setErrorMsg(
        err?.response?.status === 429
          ? "Please wait a minute before sending another message."
          : "Something went wrong — please try again, or email me directly below."
      );
      setStatus("error");
    }
  };

  if (!contact) {
    return (
      <main className="min-h-screen bg-zinc-950 flex items-center justify-center text-zinc-500 text-sm uppercase tracking-[0.3em]">
        Loading…
      </main>
    );
  }

  const inputCls =
    "w-full bg-transparent border-b border-zinc-800 focus:border-zinc-400 outline-none text-zinc-100 text-lg py-4 placeholder:text-zinc-600 transition-colors";

  return (
    <main data-testid="contact-page" className="bg-zinc-950 text-zinc-50 pt-32 md:pt-40">
      <section className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-16">
        <p className="text-[11px] uppercase tracking-[0.3em] text-zinc-500 mb-6">
          Direct contact · A note away
        </p>
        <h1
          data-testid="contact-headline"
          className="font-serif text-5xl md:text-7xl lg:text-9xl leading-[0.92] tracking-tight"
        >
          Write to me
          <br />
          <span className="italic text-zinc-400">directly.</span>
        </h1>
      </section>

      <section className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-16 py-24 md:py-32 grid grid-cols-12 gap-y-16 gap-x-12">
        <div className="col-span-12 md:col-span-7">
          <p className="text-[11px] uppercase tracking-[0.3em] text-zinc-500 mb-10">
            Send a message — briefs, festivals, books, coffee
          </p>

          {status === "sent" ? (
            <div data-testid="contact-form-success" className="border border-zinc-800 p-10 md:p-14">
              <CheckCircle2 className="h-8 w-8 text-zinc-300 mb-6" strokeWidth={1} />
              <h2 className="font-serif text-3xl md:text-4xl text-zinc-100 mb-4">
                Your note is on its way.
              </h2>
              <p className="text-zinc-400 leading-relaxed max-w-md">
                It lands directly in my inbox — I reply personally, usually within a
                few days.
              </p>
              <button
                data-testid="contact-form-send-another"
                onClick={() => setStatus("idle")}
                className="mt-8 text-[11px] uppercase tracking-[0.3em] text-zinc-300 hover:text-white link-underline pb-1"
              >
                Send another
              </button>
            </div>
          ) : (
            <form onSubmit={onSubmit} data-testid="contact-form" className="space-y-8 max-w-2xl">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-10 gap-y-8">
                <div>
                  <label htmlFor="cf-name" className="text-[10px] uppercase tracking-[0.3em] text-zinc-500">
                    Your name
                  </label>
                  <input
                    id="cf-name"
                    name="name"
                    data-testid="contact-form-name"
                    value={form.name}
                    onChange={onChange}
                    required
                    minLength={2}
                    maxLength={100}
                    placeholder="Full name"
                    className={inputCls}
                  />
                </div>
                <div>
                  <label htmlFor="cf-email" className="text-[10px] uppercase tracking-[0.3em] text-zinc-500">
                    Your email
                  </label>
                  <input
                    id="cf-email"
                    name="email"
                    type="email"
                    data-testid="contact-form-email"
                    value={form.email}
                    onChange={onChange}
                    required
                    maxLength={200}
                    placeholder="you@example.com"
                    className={inputCls}
                  />
                </div>
              </div>
              <div>
                <label htmlFor="cf-message" className="text-[10px] uppercase tracking-[0.3em] text-zinc-500">
                  The message
                </label>
                <textarea
                  id="cf-message"
                  name="message"
                  data-testid="contact-form-message"
                  value={form.message}
                  onChange={onChange}
                  required
                  minLength={10}
                  maxLength={4000}
                  rows={5}
                  placeholder="What are we making together?"
                  className={`${inputCls} resize-none`}
                />
              </div>
              {status === "error" && (
                <p data-testid="contact-form-error" className="text-red-400/90 text-sm">
                  {errorMsg}
                </p>
              )}
              <button
                type="submit"
                data-testid="contact-form-submit"
                disabled={status === "sending"}
                className="group inline-flex items-center gap-3 bg-white text-zinc-950 px-8 py-4 text-[11px] uppercase tracking-[0.3em] hover:bg-zinc-200 transition-colors disabled:opacity-60"
              >
                <Send className="h-3.5 w-3.5" strokeWidth={1.5} />
                {status === "sending" ? "Sending…" : "Send the note"}
              </button>
            </form>
          )}

          <div className="mt-20 pt-12 border-t border-zinc-900">
            <p className="text-[11px] uppercase tracking-[0.3em] text-zinc-500 mb-5">
              Or write directly
            </p>
            <a
              href={`mailto:${contact.email}`}
              data-testid="contact-email-link"
              className="font-serif italic text-3xl md:text-5xl text-zinc-50 link-underline inline-block leading-none break-all"
            >
              {contact.email}
            </a>
          </div>

          <ul className="mt-16 space-y-8 max-w-xl">
            <li className="flex items-start gap-4">
              <MapPin className="h-5 w-5 text-zinc-500 mt-1 shrink-0" strokeWidth={1.5} />
              <div>
                <p className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-1">Based in</p>
                <p data-testid="contact-location" className="text-zinc-100 text-lg">{contact.location}</p>
              </div>
            </li>
            <li className="flex items-start gap-4">
              <Mail className="h-5 w-5 text-zinc-500 mt-1 shrink-0" strokeWidth={1.5} />
              <div>
                <p className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-1">Reply time</p>
                <p className="text-zinc-100 text-lg">Personally — usually within a few days.</p>
              </div>
            </li>
          </ul>
        </div>

        <aside className="col-span-12 md:col-span-4 md:col-start-9 border-l border-zinc-900 pl-8 md:pl-12 space-y-12">
          <div>
            <p className="text-[11px] uppercase tracking-[0.3em] text-zinc-500 mb-6">
              Elsewhere
            </p>
            <ul className="space-y-3">
              {contact.socials.map((s) => (
                <li key={s.label}>
                  <a
                    href={s.url}
                    target="_blank"
                    rel="noreferrer"
                    data-testid={`contact-social-${s.label.toLowerCase().replace(/\s+/g, "-")}`}
                    className="group inline-flex items-baseline gap-3 text-zinc-100 hover:text-white"
                  >
                    <span className="font-serif text-2xl">{s.label}</span>
                    <span className="text-zinc-500 text-sm">{s.handle}</span>
                    <ArrowUpRight className="h-3 w-3 text-zinc-500 opacity-0 group-hover:opacity-100 transition-opacity" strokeWidth={1.5} />
                  </a>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <p className="text-[11px] uppercase tracking-[0.3em] text-zinc-500 mb-3">
              Studio enquiries
            </p>
            <p className="text-zinc-400 text-sm leading-relaxed">
              For brand briefs that benefit from a studio, you can also reach my
              production company directly at{" "}
              <a
                href="https://www.qissastudios.in/"
                target="_blank"
                rel="noreferrer"
                data-testid="contact-qissa-link"
                className="text-zinc-200 link-underline"
              >
                qissastudios.in
              </a>
              .
            </p>
          </div>
        </aside>
      </section>
    </main>
  );
}
